import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    AlignmentType,
    VerticalAlign,
    HeightRule,
} from "docx";
import { fixColor, fitImage, fetchImageWithDimensions, FULL_WIDTH, SCALE, createCommonHeader, createCommonFooter } from "@/utils/wordGenerator/common.js";
import { formatedDate } from "@/utils/proposalValidation.js";

export const createCPTemp4 = async (cpData, startPage) => {
    const style = cpData.style || {};
    const sections = [];
    const projectsPerPage = 2;

    // Constants (DXA)
    const HEADER_HEIGHT = 800;
    const FOOTER_HEIGHT = 800;
    const PAGE_MARGIN = 200;
    const PROJECT_BOX_HEIGHT = 7300; // Adjusted to fit

    const IMG_WIDTH_PERCENT = 45;
    const DETAILS_WIDTH_PERCENT = 53;
    const GAP_WIDTH_PERCENT = 2;
    const IMG_HEIGHT = 6000;

    const actualImgWidthDxa = Math.floor((FULL_WIDTH - (PAGE_MARGIN * 2)) * (IMG_WIDTH_PERCENT / 100));

    let currentAbsPage = startPage || 1;

    for (let i = 0; i < cpData.projects.length; i += projectsPerPage) {
        const pageProjects = cpData.projects.slice(i, i + projectsPerPage);
        const projectBoxes = [];

        for (const proj of pageProjects) {
            // 1. Image Section (Centered Cover)
            let imageCellChildren = [];
            const photo = proj.uploadedPhoto || proj.photoURL;
            if (photo) {
                const imgData = await fetchImageWithDimensions(photo);
                if (imgData) {
                    imageCellChildren = [
                        new Table({
                            width: { size: 100, type: WidthType.PERCENTAGE },
                            borders: {
                                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                            },
                            rows: [
                                new TableRow({
                                    height: { value: IMG_HEIGHT, rule: HeightRule.EXACT },
                                    children: [
                                        new TableCell({
                                            children: [new Paragraph({
                                                alignment: AlignmentType.CENTER,
                                                children: [fitImage(imgData, actualImgWidthDxa, IMG_HEIGHT, "cover")]
                                            })]
                                        })
                                    ]
                                })
                            ]
                        })
                    ];
                }
            } else {
                imageCellChildren = [
                    new Table({
                        width: { size: 100, type: WidthType.PERCENTAGE },
                        borders: {
                            top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                            left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                            insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                        },
                        rows: [
                            new TableRow({
                                height: { value: IMG_HEIGHT, rule: HeightRule.EXACT },
                                children: [
                                    new TableCell({
                                        shading: { fill: "FFFFFF" },
                                        children: []
                                    })
                                ]
                            })
                        ]
                    })
                ];
            }

            // 2. Details Section
            const detailChildren = [];

            // Project Name
            detailChildren.push(new Paragraph({
                children: [new TextRun({
                    text: proj.projectName || "N/A",
                    bold: style.heading?.bold || true,
                    italics: style.heading?.italic,
                    underline: style.heading?.underline ? {} : undefined,
                    size: (style.heading?.size || 14) * SCALE,
                    color: fixColor(style.heading?.color || "#003399"),
                    font: style.heading?.font
                })],
                spacing: { after: 120 }
            }));

            // Location
            detailChildren.push(new Paragraph({
                children: [new TextRun({
                    text: `${proj.city || 'N/A'}, ${proj.stateProvince || 'N/A'}`,
                    size: (style.label?.size || 10) * SCALE,
                    color: fixColor(style.label?.color || "#000000"),
                    font: style.label?.font,
                    bold: style.label?.bold || true,
                    italics: style.label?.italic,
                    underline: style.label?.underline ? {} : undefined,
                })],
                spacing: { after: 60 }
            }));

            // Client
            detailChildren.push(new Paragraph({
                children: [
                    new TextRun({
                        text: "Client: ",
                        bold: style.label?.bold || true,
                        italics: style.label?.italic,
                        underline: style.label?.underline ? {} : undefined,
                        size: (style.label?.size || 10) * SCALE,
                        color: fixColor(style.label?.color || "#000000"),
                        font: style.label?.font
                    }),
                    new TextRun({
                        text: proj.clientAccount?.accountName || 'N/A',
                        size: (style.text?.size || 10) * SCALE,
                        color: fixColor(style.text?.color || "#333333"),
                        font: style.text?.font,
                        bold: style.text?.bold,
                        italics: style.text?.italic,
                        underline: style.text?.underline ? {} : undefined,
                    })
                ],
                spacing: { after: 60 }
            }));

            // Designers
            const designers = proj.designerAccounts?.map(d => d.accountName).join(', ') || 'N/A';
            detailChildren.push(new Paragraph({
                children: [
                    new TextRun({
                        text: "Designers: ",
                        bold: style.label?.bold || true,
                        italics: style.label?.italic,
                        underline: style.label?.underline ? {} : undefined,
                        size: (style.label?.size || 10) * SCALE,
                        color: fixColor(style.label?.color || "#000000"),
                        font: style.label?.font
                    }),
                    new TextRun({
                        text: designers,
                        size: (style.text?.size || 10) * SCALE,
                        color: fixColor(style.text?.color || "#333333"),
                        font: style.text?.font,
                        bold: style.text?.bold,
                        italics: style.text?.italic,
                        underline: style.text?.underline ? {} : undefined,
                    })
                ],
                spacing: { after: 60 }
            }));

            // Construction Cost
            detailChildren.push(new Paragraph({
                children: [
                    new TextRun({
                        text: "Construction Cost: ",
                        bold: style.label?.bold || true,
                        italics: style.label?.italic,
                        underline: style.label?.underline ? {} : undefined,
                        size: (style.label?.size || 10) * SCALE,
                        color: fixColor(style.label?.color || "#000000"),
                        font: style.label?.font
                    }),
                    new TextRun({
                        text: proj.value ? `$${proj.value.toLocaleString()}` : 'N/A',
                        size: (style.text?.size || 10) * SCALE,
                        color: fixColor(style.text?.color || "#333333"),
                        font: style.text?.font,
                        bold: style.text?.bold,
                        italics: style.text?.italic,
                        underline: style.text?.underline ? {} : undefined,
                    })
                ],
                spacing: { after: 60 }
            }));

            // Construction Duration
            const duration = `${formatedDate(proj.constructionStartDate)} to ${formatedDate(proj.constructionCompletionDate)}`;
            detailChildren.push(new Paragraph({
                children: [
                    new TextRun({
                        text: "Construction Duration: ",
                        bold: style.label?.bold || true,
                        italics: style.label?.italic,
                        underline: style.label?.underline ? {} : undefined,
                        size: (style.label?.size || 10) * SCALE,
                        color: fixColor(style.label?.color || "#000000"),
                        font: style.label?.font
                    }),
                    new TextRun({
                        text: duration,
                        size: (style.text?.size || 10) * SCALE,
                        color: fixColor(style.text?.color || "#333333"),
                        font: style.text?.font,
                        bold: style.text?.bold,
                        italics: style.text?.italic,
                        underline: style.text?.underline ? {} : undefined,
                    })
                ],
                spacing: { after: 300 }
            }));

            // Project Details (About)
            detailChildren.push(new Paragraph({
                children: [new TextRun({
                    text: proj.projectDetails || "N/A",
                    size: (style.text?.size || 10) * SCALE,
                    color: fixColor(style.text?.color || "#333333"),
                    font: style.text?.font,
                    bold: style.text?.bold,
                    italics: style.text?.italic,
                    underline: style.text?.underline ? {} : undefined,
                })]
            }));

            // Create Project Box Inner Table (Side-by-Side)
            const innerTable = new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                    top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                    left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                    insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                },
                rows: [
                    new TableRow({
                        children: [
                            new TableCell({ width: { size: IMG_WIDTH_PERCENT, type: WidthType.PERCENTAGE }, children: imageCellChildren }),
                            new TableCell({ width: { size: GAP_WIDTH_PERCENT, type: WidthType.PERCENTAGE }, children: [] }),
                            new TableCell({ width: { size: DETAILS_WIDTH_PERCENT, type: WidthType.PERCENTAGE }, children: detailChildren }),
                        ]
                    })
                ]
            });

            projectBoxes.push(new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                    top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                    left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                    insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                },
                rows: [
                    new TableRow({
                        height: { value: PROJECT_BOX_HEIGHT, rule: HeightRule.EXACT },
                        children: [
                            new TableCell({
                                shading: { fill: fixColor(style.colorScheme?.body || "EEEEEE") },
                                margins: { top: 100, bottom: 100, left: 100, right: 100 },
                                children: [innerTable]
                            })
                        ]
                    })
                ]
            }));
        }

        // Filler for empty slots
        while (projectBoxes.length < projectsPerPage) {
            projectBoxes.push(new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                    top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                    left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                    insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                },
                rows: [
                    new TableRow({
                        height: { value: PROJECT_BOX_HEIGHT, rule: HeightRule.EXACT },
                        children: [
                            new TableCell({
                                shading: { fill: fixColor(style.colorScheme?.body || "EEEEEE") },
                                children: []
                            })
                        ]
                    })
                ]
            }));
        }

        const headerTable = createCommonHeader(cpData.title, fixColor(style.colorScheme?.header || "#545454"));
        const footerTable = createCommonFooter(currentAbsPage, fixColor(style.colorScheme?.footer || "#545454"));

        const bodyTable = new Table({
            width: { size: FULL_WIDTH, type: WidthType.DXA },
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
            },
            rows: [
                new TableRow({
                    height: { value: PROJECT_BOX_HEIGHT, rule: HeightRule.EXACT },
                    children: [
                        new TableCell({ width: { size: PAGE_MARGIN, type: WidthType.DXA }, children: [] }),
                        new TableCell({
                            width: { size: FULL_WIDTH - (PAGE_MARGIN * 2), type: WidthType.DXA },
                            children: [projectBoxes[0]],
                        }),
                        new TableCell({ width: { size: PAGE_MARGIN, type: WidthType.DXA }, children: [] }),
                    ],
                }),
                new TableRow({
                    height: { value: PROJECT_BOX_HEIGHT, rule: HeightRule.EXACT },
                    children: [
                        new TableCell({ width: { size: PAGE_MARGIN, type: WidthType.DXA }, children: [] }),
                        new TableCell({
                            width: { size: FULL_WIDTH - (PAGE_MARGIN * 2), type: WidthType.DXA },
                            children: projectBoxes[1] ? [projectBoxes[1]] : [],
                        }),
                        new TableCell({ width: { size: PAGE_MARGIN, type: WidthType.DXA }, children: [] }),
                    ],
                }),
            ],
        });

        sections.push({
            properties: {
                page: {
                    margin: { top: 0, right: 0, bottom: 0, left: 0 },
                },
            },
            children: [
                headerTable,
                new Paragraph({ spacing: { before: 80, after: 0 }, children: [] }),
                bodyTable,
                new Paragraph({ spacing: { before: 80, after: 0 }, children: [] }),
                footerTable
            ],
        });
        currentAbsPage++;
    }

    return sections;
};
